import BottomSheet from '@components/BottomSheet'
import Touchable from '@components/Touchable'
import { theme } from '@misc/theme'
import { Box, darken, Stack, useTheme } from '@mui/material'
import Typography from '@mui/material/Typography'
import LedgerScannerClient, {
  type PastBalanceInterval,
} from '@plug-core/clients/LedgerScannerClient'
import { ICP_ICP_ID } from '@plug-core/constants/tokenIds'
import useBalances from '@plug-core/hooks/useBalances'
import useConfig from '@plug-core/hooks/useConfig'
import useWallet from '@plug-core/hooks/useWallet'
import { logger } from '@plug-core/logger'
import { useBalanceChanges, useUserTokens } from '@plug-core/state'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiCheckCircle } from 'react-icons/hi'

interface BalanceChangeProps {
  totalUsdBalance: number
}

const BalanceChange: React.FC<BalanceChangeProps> = ({ totalUsdBalance }) => {
  const { t } = useTranslation()
  const { palette } = useTheme()
  const { findBalance } = useBalances()
  const { wallet } = useWallet()
  const { principalId, accountId } = wallet
  const { tokens } = useConfig()

  const { walletTotalBalanceChange, setWalletBalanceChange } = useBalanceChanges()

  const balanceChange = walletTotalBalanceChange[principalId]

  const { savedTokens } = useUserTokens()
  const tokenIds = [...(savedTokens[principalId] || []), ICP_ICP_ID]

  const fetchPastBalances = async () => {
    const relevantTokens = tokens.filter((t) => tokenIds.includes(t.id)).map((t) => t.id)

    try {
      const change = await LedgerScannerClient.fetchBalanceChange(wallet, relevantTokens)

      setWalletBalanceChange(principalId, change)
    } catch (e) {
      setWalletBalanceChange(principalId, undefined)

      logger.error('Failed to fetch balance change', { originalError: e })
    }
  }

  useEffect(() => {
    fetchPastBalances()

    const interval = setInterval(fetchPastBalances, 60 * 60 * 1000)

    return () => clearInterval(interval)
  }, [principalId, accountId, JSON.stringify(tokenIds)])

  if (!balanceChange) {
    return null
  } else {
    const usd = balanceChange
    const percentage = (usd / totalUsdBalance) * 100

    if (usd === 0) return null

    return (
      <Typography
        component="div"
        fontSize={15}
        color={percentage > 0 ? palette.rainbow.green : palette.text.red}
      >
        <Stack direction="row" alignItems="center">
          {formatMoney(usd, undefined, { signDisplay: 'exceptZero' })}
          <Box
            bgcolor={
              percentage > 0 ? darken(palette.rainbow.green, 0.75) : darken(palette.text.red, 0.75)
            }
            fontWeight="bold"
            borderRadius={0.75}
            padding="2px 6px"
            ml={0.5}
          >
            {formatNumber(percentage, '%', {
              signDisplay: 'exceptZero',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Box>
        </Stack>
      </Typography>
    )
  }
}

export default BalanceChange
